import { useMemo, useState } from 'react';
import { Loader2, Receipt } from 'lucide-react';
import type { ExpenseReceipt, ExpenseStatus } from '../../types/expense';
import { ExpenseCard } from './ExpenseCard';
import { formatMoney } from './format';

interface Props {
    receipts: ExpenseReceipt[];
    loading: boolean;
    onSelect: (r: ExpenseReceipt) => void;
}

const STATUS_FILTERS: Array<{ key: 'all' | ExpenseStatus; label: string }> = [
    { key: 'all', label: 'All' },
    { key: 'Draft', label: 'Drafts' },
    { key: 'Submitted', label: 'Submitted' },
];

export function MyExpensesList({ receipts, loading, onSelect }: Props) {
    const [status, setStatus] = useState<'all' | ExpenseStatus>('all');
    const [month, setMonth] = useState<string>('');

    const months = useMemo(() => {
        const set = new Set<string>();
        for (const r of receipts) {
            const d = r.ExpenseDate || r.CreatedAt;
            const m = /^(\d{4})-(\d{2})/.exec(d || '');
            if (m) set.add(`${m[1]}-${m[2]}`);
        }
        return Array.from(set).sort().reverse();
    }, [receipts]);

    const filtered = useMemo(() => receipts.filter(r => {
        if (status !== 'all' && r.Status !== status) return false;
        if (month) {
            const d = r.ExpenseDate || r.CreatedAt;
            if (!(d || '').startsWith(month)) return false;
        }
        return true;
    }), [receipts, status, month]);

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
                {STATUS_FILTERS.map(f => (
                    <button
                        key={f.key}
                        type="button"
                        onClick={() => setStatus(f.key)}
                        className={`rounded-full px-3 py-1 text-sm font-medium ${
                            status === f.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                        {f.label}
                    </button>
                ))}
                <select
                    value={month}
                    onChange={e => setMonth(e.target.value)}
                    className="ml-auto rounded-full border border-gray-200 bg-white px-3 py-1 text-sm text-gray-600"
                >
                    <option value="">All months</option>
                    {months.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
            </div>

            {loading ? (
                <div className="flex items-center justify-center gap-2 py-12 text-gray-400">
                    <Loader2 className="h-5 w-5 animate-spin" /> Loading…
                </div>
            ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-12 text-center text-gray-400">
                    <Receipt className="h-8 w-8" />
                    <p className="text-sm">No expenses here yet.</p>
                </div>
            ) : (
                <>
                    <p className="text-xs text-gray-400">
                        {filtered.length} receipt{filtered.length === 1 ? '' : 's'}
                    </p>
                    <div className="space-y-2">
                        {filtered.map(r => (
                            <ExpenseCard key={r.Id} receipt={r} onClick={() => onSelect(r)} />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

/** Sum gross of submitted receipts in a given YYYY-MM (helper for tiles). */
export function sumSubmittedForMonth(receipts: ExpenseReceipt[], month: string, currency = 'EUR'): string {
    const total = receipts
        .filter(r => r.Status !== 'Draft' && (r.ExpenseDate || '').startsWith(month) && r.Currency === currency)
        .reduce((acc, r) => acc + (r.GrossAmount ?? 0), 0);
    return formatMoney(total, currency);
}
